import type { EntityDict } from '@webapp2/shared/model'
import { localSettings } from '../main'
import type { BuildDownloadStatus } from '../state'
import type { RegisterModulesData, StartupResponseData } from './quest-bridge'

const handlers: Record<string, (message: VuplexMessage) => void> = {
  default: x => window.alert(`unhandled local vuplex message: ${x.type}, ${JSON.stringify(x.data)}`),
  'read-qr': () => {},
  startup: () => {
    if (window.testConfig?.emulateVuplex?.suppressStartupResponse) {
      return
    }
    const emulatedStartupResponse = localSettings?.vuplex?.emulatedStartupResponse
    dispatchVuplexMessage({
      type: 'startup-response',
      data: (window.testConfig?.emulateVuplex?.startupResponse ??
        (emulatedStartupResponse?.enabled ? emulatedStartupResponse : undefined) ??
        {}) as StartupResponseData
    })
  },
  authenticated: () => {},
  launch: () => {},
  'exit-application': () => {},
  'qr-login-result': () => {},
  'request-permissions': () => {},
  'register-modules': x => registerModules(x.data as RegisterModulesData)
}

function registerModules(data: RegisterModulesData) {
  const buildDownloadStatuses = (window.testConfig?.emulateVuplex?.buildDownloadStatuses ??
    localSettings?.vuplex?.emulateBuildDownloadStatuses) as EntityDict<BuildDownloadStatus & { delayMs?: number }>
  if (buildDownloadStatuses) {
    emulateBuildDownloadStatuses(data, buildDownloadStatuses)
  }
  if (localSettings?.vuplex?.emulateDownloadProgress) {
    emulateDownloadProgress(data)
  }
}

function emulateBuildDownloadStatuses(
  data: RegisterModulesData,
  buildDownloadStatuses: EntityDict<BuildDownloadStatus & { delayMs?: number }>
) {
  data.builds.forEach(build => {
    const entry = buildDownloadStatuses[build.id]
    if (entry) {
      const { delayMs = 0, ...status } = entry
      const dispatch = () => dispatchVuplexMessage({ type: 'build-download-status', data: status })
      if (delayMs > 0) {
        setTimeout(dispatch, delayMs)
      } else {
        dispatch()
      }
    }
  })
}

function emulateDownloadProgress(data: RegisterModulesData) {
  data.builds.forEach(build => {
    const totalBytes = 1000000000
    let downloadedBytes = 0
    setTimeout(() => {
      const interval = setInterval(() => {
        downloadedBytes += 10000000
        if (downloadedBytes >= totalBytes) {
          downloadedBytes = totalBytes
          clearInterval(interval)
        }
        dispatchVuplexMessage({
          type: 'build-download-status',
          data: {
            id: build.id,
            downloaded: downloadedBytes === totalBytes,
            downloadedBytes,
            totalBytes
          }
        })
      }, 100)
    }, 5000)
  })
}

export function emulateVuplex() {
  console.log('Emulating vuplex')
  window.vuplex = {
    postMessage: (message: VuplexMessage) => {
      window.vuplex.messages.push({ direction: 'sent', message })
      const handler = handlers[message.type] ?? handlers.default!
      handler(message)
      window.vuplex.sentVuplexMessages.push(message)
    },
    sentVuplexMessages: [],
    messages: []
  }
  // simulate vuplex handler being attached after some time period
  setTimeout(() => window.dispatchEvent(new Event('vuplexready')), 100)

  if (localSettings?.vuplex?.emulateQrCode) {
    setTimeout(
      () =>
        dispatchVuplexMessage({
          type: 'qr-code',
          data: localSettings?.vuplex?.emulateQrCode
        }),
      1500
    )
  }
}

function dispatchVuplexMessage(message: VuplexMessage) {
  window.vuplex?.messages.push({ direction: 'received', message })
  window.dispatchEvent(
    new MessageEvent('vuplexmessage', {
      data: JSON.stringify(message)
    })
  )
}
